// Vue
import { computed, ref, watch, Ref } from "vue";

// Dependencies
import { v4 as uuidv4 } from "uuid";
import { defineStore } from "pinia";

// Types
import {
  UpsertConstruction,
  ConstructionMaterial,
  ConstructionInhomogeneousLayer,
} from "@/types/Views/CreateConstruction/NewConstruction";

// Services
import { constructionTemplate } from "@/services/Views/CreateConstruction/ConstructionTemplate";
import { Unit, useGetUnitsQuery } from "@/graphql";
import calculateInhomogeneousLayerWeight from "@/features/ConstructionAmountCalculator/utils/calculateInhomogeneousLayerWeight";
import { FromUnitInput } from "@/features/ConstructionAmountCalculator/types";
import ConstructionAmountCalculator from "@/features/ConstructionAmountCalculator";
import UnitId from "@/features/ConstructionAmountCalculator/constants/UnitId";
import { getChangesFromOriginal } from "@/views/Library/Construction/useConstructionDirtyState";
import withApolloClient from "@/graphql/services/withApolloClient";

type State = "create" | "edit" | "clone";

// Allowed declare unit ids for constructions (in sorted order)
export const ConstructionDeclaredUnitIdWhitelist = [UnitId.M, UnitId.M2, UnitId.M3, UnitId.KG, UnitId.PIECE];

interface NewConstructionStore {
  // Refs
  construction: Ref<UpsertConstruction>;
  declaredUnitLabels: Ref<Unit | undefined>;
  name: Ref<string>;
  description: Ref<string>;
  fromUnitInput: Ref<FromUnitInput | undefined>;
  // Set true on the first submit attempt so header fields (name, declared unit) show their
  // inline errors — mirrors the Create Material form's wasValidated pattern (RTLCA-5077).
  wasValidated: Ref<boolean>;

  // Getters
  getAllowedDeclaredUnits: () => Array<{
    name: string;
    value: any;
    disabled?: boolean;
  }>;
  getConstruction: () => UpsertConstruction;
  getConstructionName: () => string;
  getConstructionDescription: () => string;
  getDeclaredUnit: () => UpsertConstruction["declaredUnit"];
  getDeclaredUnitLabels: () => Unit | undefined;
  getConstructionMaterialScaleDenominator: () => number;
  getTotalWeight: () => number;
  getDeclaredValue: () => number;

  // Setters
  setName: (newName: string) => void;
  setDescription: (newDescription: string) => void;
  setWasValidated: (value: boolean) => void;
  setConstruction: (newConstruction: UpsertConstruction) => void;
  setState: (newState: State) => void;
  getInhomogeneousLayerPercentage: (inhomogeneousLayerId: string) => number;
  getMaterialPercentage: (materialId: string, inhomogeneousLayerId?: string) => number;
  canAddMaterials: () => boolean;
  addMaterial: (material: ConstructionMaterial["material"], lifetime: number) => Promise<void>;
  addMaterialToInhomogeneousLayer: (
    inhomogeneousLayerId: string,
    material: ConstructionMaterial["material"],
    lifetime: number
  ) => Promise<void>;
  convertMaterialToInhomogeneousLayer: (
    existingMaterial: ConstructionMaterial,
    newMaterial: ConstructionMaterial["material"],
    lifetime: number
  ) => Promise<void>;
  removeMaterial: (id: string) => void;
  removeMaterialFromInhomogeneousLayer: (inhomogeneousLayerId: string, materialId: string) => void;
  removeInhomogeneousLayer: (id: string) => void;
  getInhomogeneousLayerName: (inhomogeneousLayer: ConstructionInhomogeneousLayer) => string;
  getInhomogeneousLayerDescription: (inhomogeneousLayer: ConstructionInhomogeneousLayer) => string;
  setInhomogeneousLayerName: (inhomogeneousLayer: ConstructionInhomogeneousLayer, name: string) => void;
  setInhomogeneousLayerDescription: (inhomogeneousLayer: ConstructionInhomogeneousLayer, description: string) => void;
  getConstructionMaterial: (id: string, inhomogeneousLayerId?: string) => ConstructionMaterial | undefined;
  updateInhomogeneousLayerThickness: (inhomogeneousLayerId: string, thickness: number) => void;
  updateMaterial: (constructionMaterial: ConstructionMaterial, inhomogeneousLayerId?: string) => void;

  // Methods (state)
  isCreating: () => boolean;
  isEditing: () => boolean;
  isCloning: () => boolean;
  reset: () => void;

  // Dirty state (shared for route guard, modals, etc.)
  constructionIsDirty: Ref<boolean>;
  initConstructionOriginal: () => void;
}

export const useNewConstruction = defineStore("useNewConstruction", (): NewConstructionStore => {
  // ///////////////////
  // Refs
  // ///////////////////

  const construction = ref<UpsertConstruction>(JSON.parse(JSON.stringify(constructionTemplate)));
  const originalConstruction = ref<UpsertConstruction | null>(null);
  const createConstructionPageState = ref<State>("create");
  const name = ref<string>("");
  const description = ref<string>("");
  const wasValidated = ref<boolean>(false);
  const declaredUnitLabels = ref<Unit>();
  const fromUnitInput = ref<FromUnitInput>();

  // ///////////////////
  // Queries
  // ///////////////////

  const { result: unitResult } = withApolloClient(() => useGetUnitsQuery());

  // ///////////////////
  // Getters
  // //////////////////

  const getConstruction = () => {
    return construction.value;
  };

  const getConstructionName = () => {
    return name.value;
  };

  const getConstructionDescription = () => {
    return description.value;
  };

  const getAllowedDeclaredUnits = () => {
    return (
      unitResult?.value?.unit
        .filter((x) => ConstructionDeclaredUnitIdWhitelist.includes(x.id))
        .map((x) => ({ name: `${x.label} (${x.fullName})`, value: x.id }))
        .sort(
          (a, b) => ConstructionDeclaredUnitIdWhitelist.indexOf(a.value) - ConstructionDeclaredUnitIdWhitelist.indexOf(b.value)
        ) || []
    );
  };

  const getDeclaredUnit = () => {
    return construction.value.declaredUnit;
  };

  const getDeclaredUnitLabels = () => {
    return declaredUnitLabels.value;
  };

  // ///////////////////
  // Setters
  // ///////////////////

  const setName = (newName: string) => {
    name.value = newName;
  };

  const setDescription = (newDescription: string) => {
    description.value = newDescription;
  };

  const setWasValidated = (value: boolean) => {
    wasValidated.value = value;
  };

  const setConstruction = (newConstruction: UpsertConstruction) => {
    construction.value = newConstruction;
  };

  const setState = (newState: State) => {
    createConstructionPageState.value = newState;
  };

  // ///////////////////
  // Methods (materials)
  // ///////////////////

  const canAddMaterials = () => {
    return name.value.length > 2 && construction.value.declaredUnit.unitId > -1;
  };

  const addMaterial = async (newMaterial: ConstructionMaterial["material"], lifetime: number) => {
    // Create a new construction material
    const constructionMaterial: ConstructionMaterial = {
      id: uuidv4(),
      materialId: newMaterial.id,
      lifetime,
      material: newMaterial,
      sorting: {
        index: -1,
      },
      amount: -1,
    };

    // Update the construction
    setConstruction({
      ...getConstruction(),
      materials: [...getConstruction().materials, constructionMaterial],
    });
  };

  const addMaterialToInhomogeneousLayer = async (
    inhomogeneousLayerId: string,
    newMaterial: ConstructionMaterial["material"],
    lifetime: number
  ) => {
    // Create a new construction material
    const constructionMaterial: ConstructionMaterial = {
      id: uuidv4(),
      materialId: newMaterial.id,
      lifetime,
      material: newMaterial,
      sorting: {
        index: -1,
      },
      amount: -1,
    };

    // Add the material to the inhomogeneous layer
    const inhomogeneousLayers = getConstruction().inhomogeneousLayers.map((layer) =>
      layer.id === inhomogeneousLayerId
        ? {
            ...layer,
            materials: [...layer.materials, constructionMaterial],
          }
        : layer
    );

    // Update the construction
    setConstruction({
      ...getConstruction(),
      inhomogeneousLayers,
    });
  };

  const convertMaterialToInhomogeneousLayer = async (
    existingMaterial: ConstructionMaterial,
    newMaterial: ConstructionMaterial["material"],
    lifetime: number
  ) => {
    try {
      // Create a new construction material
      const constructionMaterial: ConstructionMaterial = {
        id: uuidv4(),
        materialId: newMaterial.id,
        lifetime,
        material: newMaterial,
        sorting: {
          index: -1,
        },
        amount: -1,
      };

      // Create new inhomogeneous layer
      const constructionInhomogeneousLayer: ConstructionInhomogeneousLayer = {
        id: uuidv4(),
        amount: 1,
        descriptions: [],
        materials: [existingMaterial, constructionMaterial],
        names: [],
        sorting: {
          index: -1,
        },
        thickness: 1,
      };

      // Remove the existing material
      const materials = getConstruction().materials.filter((m) => m.id !== existingMaterial.id);

      // Create new inhomogeneous layer
      const inhomogeneousLayers = [
        // Existing inhomogeneous layers
        ...getConstruction().inhomogeneousLayers,
        constructionInhomogeneousLayer,
      ];

      // Update construction
      setConstruction({
        ...getConstruction(),
        materials,
        inhomogeneousLayers,
      });
    } catch (_) {
      throw new Error("Error creating inhomogeneous layer");
    }
  };

  const removeMaterial = (id: string) => {
    const materials = getConstruction().materials.filter((material) => material.id !== id);
    setConstruction({
      ...getConstruction(),
      materials,
    });
  };

  const getConstructionMaterial = (constructionMaterialId: string, inhomogeneousLayerId?: string) => {
    if (inhomogeneousLayerId) {
      return construction.value.inhomogeneousLayers
        .find((x) => x.id === inhomogeneousLayerId)
        ?.materials.find((x) => x.id === constructionMaterialId);
    }

    return construction.value.materials.find((x) => x.id === constructionMaterialId);
  };

  const updateInhomogeneousLayerThickness = (inhomogeneousLayerId: string, amount: number) => {
    const inhomogeneousLayers = construction.value.inhomogeneousLayers.map((layer) => {
      if (layer.id === inhomogeneousLayerId) {
        const newLayer = { ...layer, amount };

        const materials = layer.materials.map((material) => {
          const result = ConstructionAmountCalculator.fromInhomogeneousLayer(
            newLayer,
            construction.value.declaredUnit.unitId
          ).byInhomogeneousLayerThickness(material);

          return {
            ...material,
            ...result.dimensions,
            amount: result.amount,
            weight: result.weight,
          };
        });

        return { ...newLayer, materials };
      }

      return layer;
    });

    setConstruction({
      ...getConstruction(),
      inhomogeneousLayers,
    });
  };

  const updateMaterial = (constructionMaterial: ConstructionMaterial, inhomogeneousLayerId?: string) => {
    let materials = construction.value.materials;
    let inhomogeneousLayers = construction.value.inhomogeneousLayers;

    if (inhomogeneousLayerId) {
      inhomogeneousLayers = inhomogeneousLayers.map((layer) => {
        if (layer.id === inhomogeneousLayerId) {
          return {
            ...layer,
            materials: layer.materials.map((material) =>
              material.id === constructionMaterial.id ? constructionMaterial : material
            ),
          };
        }
        return layer;
      });
    } else {
      materials = materials.map((material) => {
        return material.id === constructionMaterial.id ? { ...constructionMaterial } : material;
      });
    }

    setConstruction({
      ...getConstruction(),
      materials,
      inhomogeneousLayers,
    });
  };

  const removeMaterialFromInhomogeneousLayer = (inhomogeneousLayerId: string, materialId: string) => {
    // Remove the material from the inhomogeneous layer
    const inhomogeneousLayers = getConstruction().inhomogeneousLayers.map((layer) =>
      layer.id === inhomogeneousLayerId
        ? {
            ...layer,
            materials: layer.materials.filter((material) => material.id !== materialId),
          }
        : layer
    );

    // Update the construction
    setConstruction({
      ...getConstruction(),
      inhomogeneousLayers,
    });

    // If no materials are present then the inhomogeneous layer should be removed
    if (inhomogeneousLayers.find((layer) => layer.id === inhomogeneousLayerId)?.materials.length === 0) {
      removeInhomogeneousLayer(inhomogeneousLayerId);
      return;
    }
  };

  const removeInhomogeneousLayer = (id: string) => {
    const inhomogeneousLayers = getConstruction().inhomogeneousLayers.filter((layer) => layer.id !== id);
    setConstruction({
      ...getConstruction(),
      inhomogeneousLayers,
    });
  };

  const getInhomogeneousLayerName = (inhomogeneousLayer: ConstructionInhomogeneousLayer) => {
    return inhomogeneousLayer.names[0]?.label || "";
  };

  const getInhomogeneousLayerDescription = (inhomogeneousLayer: ConstructionInhomogeneousLayer) => {
    return inhomogeneousLayer.descriptions[0]?.label || "";
  };

  const getInhomogeneousLayerPercentage = (inhomogeneousLayerId: string) => {
    const inhomogeneousLayer = inhomogeneousLayerId
      ? construction.value.inhomogeneousLayers.find((x) => x.id === inhomogeneousLayerId)
      : undefined;

    if (!inhomogeneousLayer) {
      return 0;
    }

    const totalWeight = getTotalWeight();

    const weight = calculateInhomogeneousLayerWeight(inhomogeneousLayer!, UnitId.M3);

    if (weight === 0 || totalWeight === 0) {
      return 0;
    }

    return (weight / totalWeight) * 100;
  };

  const getMaterialPercentage = (materialId: string, inhomogeneousLayerId?: string) => {
    const inhomogeneousLayer = inhomogeneousLayerId
      ? construction.value.inhomogeneousLayers.find((x) => x.id === inhomogeneousLayerId)
      : undefined;

    if (inhomogeneousLayerId && !inhomogeneousLayer) {
      return 0;
    }

    const totalWeight = inhomogeneousLayerId
      ? calculateInhomogeneousLayerWeight(inhomogeneousLayer!, UnitId.M3)
      : getTotalWeight();
    const material = getConstructionMaterial(materialId, inhomogeneousLayerId);

    if (!material) {
      return 0;
    }

    const calculatorResult = ConstructionAmountCalculator.fromConstruction(construction.value).calculate(material);
    const weight = calculatorResult.weight;

    if (weight === 0 || totalWeight === 0) {
      return 0;
    }

    return (weight / totalWeight) * 100;
  };

  // declared value
  const getDeclaredValue = () => {
    if (declaredUnitLabels.value?.id === UnitId.KG) {
      return getTotalWeight();
    }

    return 1;
  };

  const getConstructionMaterialScaleDenominator = () => {
    // If declared unit is kg, calculate the total weight as the declared value
    if (declaredUnitLabels.value?.id === UnitId.KG) {
      return getTotalWeight();
    }

    const calculator = ConstructionAmountCalculator.fromConstruction(construction.value);

    // If declared unit is m3, calculate the total volume as the declared value
    if (declaredUnitLabels.value?.id === UnitId.M3) {
      // Calculate cubic meters
      const materialSum = construction.value.materials.reduce<number>((acc, constructionMaterial) => {
        return acc + calculator.toConstructionMaterial(constructionMaterial).getToCubicMeters(constructionMaterial);
      }, 0);
      const inhomogeneousLayerSum = construction.value.inhomogeneousLayers.reduce<number>((acc, inhomogeneousLayer) => {
        return (
          acc +
          inhomogeneousLayer.materials.reduce<number>((acc, constructionMaterial) => {
            return acc + calculator.toConstructionMaterial(constructionMaterial).getToCubicMeters(constructionMaterial);
          }, 0)
        );
      }, 0);

      return materialSum + inhomogeneousLayerSum;
    }

    return 1;
  };

  const getTotalWeight = () => {
    const inhomogeneousLayersTotalWeight = construction.value.inhomogeneousLayers.reduce((acc, inhomogeneousLayer) => {
      const weight = calculateInhomogeneousLayerWeight(inhomogeneousLayer, construction.value.declaredUnit.unitId);

      return acc + weight;
    }, 0);

    const constructionMaterialsTotalWeight = construction.value.materials.reduce((acc, constructionMaterial) => {
      const converter = ConstructionAmountCalculator.fromConstruction(construction.value).toConstructionMaterial(
        constructionMaterial
      );
      const weight = converter.calculate(constructionMaterial).weight;

      return acc + weight;
    }, 0);

    return inhomogeneousLayersTotalWeight + constructionMaterialsTotalWeight;
  };

  const setInhomogeneousLayerName = (inhomogeneousLayer: ConstructionInhomogeneousLayer, name: string) => {
    inhomogeneousLayer.names = [{ label: name, languageCode: "en", translationSource: "user" }];
  };

  const setInhomogeneousLayerDescription = (inhomogeneousLayer: ConstructionInhomogeneousLayer, description: string) => {
    inhomogeneousLayer.descriptions = [{ label: description, languageCode: "en", translationSource: "user" }];
  };

  // ///////////////////
  // Methods (state)
  // ///////////////////

  const isCreating = () => {
    return createConstructionPageState.value === "create";
  };

  const isEditing = () => {
    return createConstructionPageState.value === "edit";
  };

  const isCloning = () => {
    return createConstructionPageState.value === "clone";
  };

  function reset() {
    construction.value = JSON.parse(JSON.stringify(constructionTemplate));
    originalConstruction.value = null;
    createConstructionPageState.value = "create";
    name.value = "";
    description.value = "";
    wasValidated.value = false;
  }

  const constructionIsDirty = computed(() => {
    if (!originalConstruction.value) return false;
    return getChangesFromOriginal(construction.value, originalConstruction.value).length > 0;
  });

  function initConstructionOriginal() {
    originalConstruction.value = JSON.parse(JSON.stringify(construction.value));
  }

  // ///////////////////
  // Watchers
  // ///////////////////

  // Construction name
  watch(name, (newName) => {
    construction.value.names = [{ label: newName, languageCode: "en", translationSource: "user" }];
  });

  // Construction description
  watch(description, (newDescription) => {
    construction.value.descriptions = [
      {
        label: newDescription,
        languageCode: "en",
        translationSource: "user",
      },
    ];
  });

  // Declared unit
  watch([unitResult, () => construction.value.declaredUnit.unitId], ([newUnits, newDeclaredUnitId]) => {
    declaredUnitLabels.value = newUnits?.unit.find((unit) => unit.id == newDeclaredUnitId);
  });

  // From unit (ConstructionAmountCalculator)
  watch(
    () => construction.value.declaredUnit,
    (newDeclaredUnit) => {
      fromUnitInput.value = {
        amount: 1,
        declaredUnitId: newDeclaredUnit.unitId,
        isInhomogeneousLayer: false,
      };
    },
    { deep: true, immediate: true }
  );

  return {
    // Refs
    construction,
    declaredUnitLabels,
    name,
    description,
    fromUnitInput,
    wasValidated,

    // Getters
    getAllowedDeclaredUnits,
    getConstruction,
    getConstructionName,
    getConstructionDescription,
    getDeclaredUnit,
    getDeclaredUnitLabels,
    getTotalWeight,

    // Setters
    setName,
    setDescription,
    setConstruction,
    setState,
    setWasValidated,

    // Methods (materials)
    canAddMaterials,
    getInhomogeneousLayerPercentage,
    getMaterialPercentage,
    addMaterial,
    addMaterialToInhomogeneousLayer,
    convertMaterialToInhomogeneousLayer,
    removeMaterial,
    removeMaterialFromInhomogeneousLayer,
    removeInhomogeneousLayer,
    getInhomogeneousLayerName,
    getInhomogeneousLayerDescription,
    setInhomogeneousLayerName,
    setInhomogeneousLayerDescription,
    getConstructionMaterial,
    getConstructionMaterialScaleDenominator,
    getDeclaredValue,
    updateInhomogeneousLayerThickness,
    updateMaterial,

    // Methods (state)
    isCreating,
    isEditing,
    isCloning,
    reset,

    // Dirty state
    constructionIsDirty,
    initConstructionOriginal,
  };
});
